import * as React from 'react';

import type {Status, AnalyticsSnippetVersion} from 'nutshell-graphql-types';

import {useOpenableModal} from '../../../../openable-modals';
import {EMBED_ANALYTICS_MODAL} from '../../../../openable-modals/openable-modals';

import {ConnectedFormsActiveBody} from './connected-forms-active-body';
import {getActiveText} from './consts';
import {NewVersionAvailableBody} from './new-version-available-body';
import {NoVisitsBody} from './no-visits-body';
import {PrimaryConfigureCtaButton} from './primary-configure-cta-button';
import {TrackingSnippetNotConfiguredBody} from './tracking-snippet-not-configured-body';
import type {PageType} from './types';

import styles from './analytics-status.module.css';

type Props = {
    status: Status;
    snippetVersion: AnalyticsSnippetVersion;
    numActiveForms: number;
    numDraftForms: number;
    onClosePopover: () => void;
    includeSeeAll?: boolean;
    pageType: PageType;
};

export function StatusBody(props: Props) {
    const {onOpen: onOpenEmbedAnalyticsModal} = useOpenableModal(EMBED_ANALYTICS_MODAL);

    const getBody = (status: Status, pageType: PageType) => {
        if (status === 'CONFIGURED') {
            switch (pageType) {
                case 'connected-forms':
                    return (
                        <ConnectedFormsActiveBody
                            numActiveForms={props.numActiveForms}
                            numDraftForms={props.numDraftForms}
                            includeSeeAll={props.includeSeeAll}
                        />
                    );
                case 'chat':
                    return (
                        <div className='flex mt-16 justify-center flex-dir-col'>
                            {getActiveText(pageType)}
                            <PrimaryConfigureCtaButton
                                pageType={pageType}
                                status={status}
                                onClosePopover={props.onClosePopover}
                                onOpenEmbedAnalyticsModal={onOpenEmbedAnalyticsModal}
                            />
                        </div>
                    );
                default:
                    return (
                        <div className='flex pad-16 justify-center'>{getActiveText(pageType)}</div>
                    );
            }
        } else if (status === 'NEW_VERSION_AVAILABLE') {
            return (
                <NewVersionAvailableBody
                    pageType={props.pageType}
                    status={props.status}
                    onOpenEmbedAnalyticsModal={onOpenEmbedAnalyticsModal}
                    onClosePopover={props.onClosePopover}
                />
            );
        } else if (status === 'NEVER_CONFIGURED') {
            return (
                <TrackingSnippetNotConfiguredBody
                    pageType={props.pageType}
                    status={props.status}
                    onOpenEmbedAnalyticsModal={onOpenEmbedAnalyticsModal}
                    onClosePopover={props.onClosePopover}
                />
            );
        } else {
            return (
                <NoVisitsBody
                    pageType={props.pageType}
                    status={props.status}
                    onOpenEmbedAnalyticsModal={onOpenEmbedAnalyticsModal}
                    onClosePopover={props.onClosePopover}
                />
            );
        }
    };

    return (
        <>
            <div className={styles.body}>{getBody(props.status, props.pageType)}</div>
            <div className={styles['version-text']}>
                {getSnippetVersionText(props.status, props.snippetVersion)}
            </div>
        </>
    );
}

// Mostly useful for support debugging
function getSnippetVersionText(status: Status, snippetVersion: AnalyticsSnippetVersion) {
    if (status === 'NEVER_CONFIGURED') {
        return 'Snippet version: None detected';
    }

    switch (snippetVersion) {
        case 'VERSION_1':
            return 'Snippet version: 1.0';
        case 'VERSION_2':
            return 'Snippet version: 2.0';
        case 'VERSION_1_5':
            return 'Snippet version: 1.5';
        default:
            return 'Snippet version: None detected';
    }
}
